import React, { useEffect, useRef, useState } from "react";
import { Reply, Copy, Forward, Pin, Star, StickyNote, Flag, Trash2, Plus, X, FileText, Download, Paperclip, Image as ImageIcon, Camera, Video, File as FileIcon, Mic, Play, Pause, Check, CheckCheck, Info, Pencil, Smile, ChevronDown } from "lucide-react";
import { api, imgUrl } from "../../lib/api";
import { fmtSize, checkFile, fileKind, uploadFile } from "../../lib/chatUpload";

export const QUICK_EMOJI = ["❤️", "😂", "😮", "😢", "🙏"];
const MORE_EMOJI = ["👍", "👎", "🔥", "🎉", "😍", "🥰", "😊", "😁", "🤔", "😅", "👏", "💯", "✅", "💵", "💰", "🙌", "😎", "🤝", "😡", "😭", "👀", "⭐", "💪", "🙂"];

export const ACTIONS = {
  info: [Info, "Message info"], reply: [Reply, "Reply"], copy: [Copy, "Copy"], react: [Smile, "React"], download: [Download, "Download"], edit: [Pencil, "Edit"], forward: [Forward, "Forward"], pin: [Pin, "Pin"], star: [Star, "Star"],
  note: [StickyNote, "Add text to note"], report: [Flag, "Report"], delete: [Trash2, "Delete"],
};

export const useLongPress = (cb) => {
  const t = useRef(null);
  const clear = () => clearTimeout(t.current);
  return {
    onContextMenu: (e) => { e.preventDefault(); cb(e.clientX, e.clientY); },
    onTouchStart: (e) => { const p = e.touches[0]; t.current = setTimeout(() => cb(p.clientX, p.clientY), 480); },
    onTouchEnd: clear, onTouchMove: clear,
  };
};

export const MessageMenu = ({ menu, actions, labels = {}, onAction, onReact, onClose, noReact = false }) => {
  const [more, setMore] = useState(false);
  useEffect(() => { setMore(false); }, [menu]);
  if (!menu) return null;
  const w = 240, h = 60 + actions.length * 44 + (more ? 110 : 0);
  const left = Math.max(8, Math.min(menu.x, window.innerWidth - w - 8));
  const top = Math.max(8, Math.min(menu.y, window.innerHeight - h - 8));
  return (
    <div className="fixed inset-0 z-[90]" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} data-testid="message-menu-overlay">
      <div className="fixed flex flex-col gap-2 sw-fade-up" style={{ left, top, width: w }} onClick={(e) => e.stopPropagation()}>
        <div className="overflow-hidden rounded-2xl border border-[#e7eeec] bg-white py-1.5 shadow-[0_18px_50px_-12px_rgba(15,46,42,0.35)]" data-testid="message-menu">
          {actions.map((k) => {
            const [I, label] = ACTIONS[k];
            return (
              <button key={k} onClick={() => { if (k === "react") { setMore(true); return; } onAction(k); onClose(); }} className={`flex w-full items-center gap-3.5 px-4 py-2.5 text-left text-[15px] transition-colors hover:bg-[#f2f7f6] ${k === "delete" || k === "report" ? (k === "delete" ? "border-t border-[#eef2f1] text-[#c0362c]" : "text-[#1f3a36]") : "text-[#1f3a36]"}`} data-testid={`msg-action-${k}`}>
                <I size={18} strokeWidth={1.8} />{labels[k] || label}
              </button>
            );
          })}
        </div>
        {!noReact && <div className="rounded-full border border-[#e7eeec] bg-white px-2 py-1.5 shadow-lg" data-testid="message-emoji-bar">
          <div className="flex items-center justify-between">
            {QUICK_EMOJI.map((e) => <button key={e} onClick={() => { onReact(e); onClose(); }} className="rounded-full p-1 text-[22px] leading-none transition-transform hover:scale-125" data-testid="msg-react-quick">{e}</button>)}
            <button onClick={() => setMore(!more)} className="rounded-full p-1.5 text-[#4b5d5a] hover:bg-[#f2f7f6]" aria-label="More emoji" data-testid="msg-react-more"><Plus size={20} /></button>
          </div>
          {more && (
            <div className="grid grid-cols-8 gap-0.5 border-t border-[#eef2f1] pt-1.5" data-testid="msg-emoji-grid">
              {MORE_EMOJI.map((e) => <button key={e} onClick={() => { onReact(e); onClose(); }} className="rounded-lg p-0.5 text-[20px] hover:bg-[#f2f7f6]">{e}</button>)}
            </div>
          )}
        </div>}
      </div>
    </div>
  );
};

export const MessageAttachments = ({ m, onZoom, mine }) => {
  const atts = m.attachments || [];
  const legacy = (m.images || []).filter((u) => !atts.some((a) => a.url === u)).map((u) => ({ url: u, kind: "image", id: u }));
  const imgs = [...legacy, ...atts.filter((a) => a.kind === "image")];
  const vids = atts.filter((a) => a.kind === "video");
  const docs = atts.filter((a) => a.kind === "file");
  const auds = atts.filter((a) => a.kind === "audio");
  if (!imgs.length && !vids.length && !docs.length && !auds.length) return null;
  return (
    <div className="mb-1.5 space-y-1.5">
      {imgs.length > 0 && (
        <div className={`grid gap-1.5 ${imgs.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {imgs.map((a) => (
            <button type="button" key={a.id} onClick={() => onZoom(imgUrl(a.url))} className="overflow-hidden rounded-lg bg-black/5">
              <img src={imgUrl(a.url)} alt={a.name || "Attachment"} className="h-28 w-full min-w-[100px] object-cover" data-testid="chat-attachment-image" />
            </button>
          ))}
        </div>
      )}
      {auds.map((a) => <VoicePlayer key={a.id} a={a} mine={mine} />)}
      {vids.map((a) => <video key={a.id} src={imgUrl(a.url)} controls playsInline preload="metadata" className="max-h-56 w-full rounded-lg bg-black" data-testid="chat-attachment-video" />)}
      {docs.map((a) => (
        <a key={a.id} href={`${imgUrl(a.url)}?download=1`} target="_blank" rel="noreferrer" className={`flex items-center gap-2.5 rounded-xl px-3 py-2 ${mine ? "bg-white/15" : "bg-[#f2f7f6]"}`} data-testid="chat-attachment-file">
          <FileText size={22} className="shrink-0" />
          <span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-semibold">{a.name}</span><span className="text-[11px] opacity-75">{fmtSize(a.size)}</span></span>
          <Download size={16} className="shrink-0" />
        </a>
      ))}
    </div>
  );
};

export const ReplyQuote = ({ r, mine, label }) => !r ? null : (
  <div className={`mb-1.5 rounded-lg border-l-[3px] px-2.5 py-1.5 text-[12.5px] ${mine ? "border-white/70 bg-white/15 text-white/90" : "border-[#0e8c7f] bg-[#eef7f5] text-[#3d504d]"}`} data-testid="chat-reply-quote">
    <div className="font-bold">{label}</div>
    <div className="line-clamp-2">{r.text || (r.kind ? `[${r.kind}]` : "")}</div>
  </div>
);

export const Reactions = ({ reactions, mine }) => {
  const list = Object.values(reactions || {}).filter(Boolean);
  if (!list.length) return null;
  return <div className={`-mt-2 flex ${mine ? "justify-end pr-2" : "justify-start pl-2"}`}><span className="relative z-[1] rounded-full border border-[#e3efec] bg-white px-1.5 py-0.5 text-[14px] shadow-sm" data-testid="chat-reactions">{list.join(" ")}</span></div>;
};

export const ReplyBar = ({ reply, label, onCancel }) => !reply ? null : (
  <div className="mb-2 flex items-start gap-2 rounded-xl border-l-[3px] border-[#0e8c7f] bg-[#eef7f5] px-3 py-2 text-[12.5px]" data-testid="chat-replying-bar">
    <div className="min-w-0 flex-1"><div className="font-bold text-[#0b6f65]">{label}</div><div className="truncate text-[#3d504d]">{reply.text || "[attachment]"}</div></div>
    <button type="button" onClick={onCancel} className="text-[#6b7c79]" aria-label="Cancel reply" data-testid="chat-reply-cancel"><X size={15} /></button>
  </div>
);

export const PendingFiles = ({ items, onRemove }) => !items.length ? null : (
  <div className="mb-2 flex gap-2 overflow-x-auto pb-1" data-testid="chat-attachments">
    {items.map((f, i) => (
      <div key={f.key} className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-[#dcebe8] bg-[#f4f8f7]">
        {f.kind === "image" ? <img src={f.preview} alt="" className="h-full w-full object-cover" />
          : f.kind === "video" ? <video src={f.preview} muted className="h-full w-full object-cover" />
          : <div className="flex h-full flex-col items-center justify-center p-1 text-[#0b6f65]"><FileIcon size={18} /><span className="mt-0.5 w-full truncate text-center text-[9.5px]">{f.file.name}</span></div>}
        {f.kind === "video" && <Video size={14} className="absolute bottom-1 left-1 text-white drop-shadow" />}
        {f.progress != null && (
          <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/20"><div className="h-full bg-[#0e8c7f] transition-[width]" style={{ width: `${f.progress}%` }} data-testid="chat-upload-progress" /></div>
        )}
        {f.progress == null && <button type="button" onClick={() => onRemove(i)} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white" aria-label="Remove attachment"><X size={12} /></button>}
      </div>
    ))}
    <span className="self-center text-[12px] text-[#6b7c79]">{items.length}/5</span>
  </div>
);

const PICKS = [
  ["media", ImageIcon, "Photos & videos", "image/*,video/*", undefined],
  ["camera", Camera, "Take photo", "image/*", "environment"],
  ["record", Video, "Record video", "video/*", "environment"],
  ["doc", FileIcon, "Document", ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.rtf,.odt,.ods,.zip", undefined],
];

export const AttachMenu = ({ onFiles, disabled, t = (s) => s, up = true, allow = null }) => {
  const [open, setOpen] = useState(false);
  const refs = useRef({});
  return (
    <div className="relative shrink-0">
      <button type="button" disabled={disabled} onClick={() => setOpen(!open)} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#dcebe8] text-[#0e8c7f] transition-colors hover:bg-[#effaf7] disabled:opacity-40" aria-label={t("Attach")} data-testid="chat-attach-button"><Paperclip size={18} /></button>
      {open && (
        <>
          <div className="fixed inset-0 z-[5]" onClick={() => setOpen(false)} />
          <div className={`absolute left-0 z-[6] w-52 overflow-hidden rounded-2xl border border-[#e7eeec] bg-white py-1.5 shadow-xl sw-fade-up ${up ? "bottom-[52px]" : "top-[52px]"}`} data-testid="chat-attach-menu">
            {PICKS.filter(([k]) => !allow || allow.includes(k)).map(([k, I, label]) => (
              <button key={k} type="button" onClick={() => { refs.current[k]?.click(); setOpen(false); }} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-[#1f3a36] hover:bg-[#f2f7f6]" data-testid={`attach-${k}`}>
                <I size={17} className="text-[#0e8c7f]" />{t(label)}
              </button>
            ))}
          </div>
        </>
      )}
      {PICKS.map(([k, , , accept, capture]) => (
        <input key={k} ref={(el) => (refs.current[k] = el)} type="file" accept={accept} capture={capture} multiple={!capture} className="hidden"
          onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid={`chat-file-input-${k}`} />
      ))}
    </div>
  );
};

export const usePendingFiles = (notify, t = (s) => s) => {
  const [items, setItems] = useState([]);
  const add = (files) => {
    const ok = [];
    for (const f of files) {
      const err = checkFile(f);
      if (err) { notify(err); continue; }
      ok.push({ key: `${f.name}-${f.size}-${Math.random()}`, file: f, kind: fileKind(f), preview: URL.createObjectURL(f), progress: null });
    }
    setItems((prev) => {
      const merged = [...prev, ...ok];
      if (merged.length > 5) notify(t("You can attach up to 5 files per message"));
      return merged.slice(0, 5);
    });
  };
  const remove = (i) => setItems((prev) => prev.filter((_, j) => j !== i));
  const uploadAll = async (sid) => {
    const out = [];
    for (let i = 0; i < items.length; i++) {
      const setP = (p) => setItems((prev) => prev.map((x, j) => (j === i ? { ...x, progress: p } : x)));
      setP(0);
      out.push(await uploadFile(items[i].file, { sid, onProgress: setP }));
    }
    return out;
  };
  const reset = () => setItems([]);
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); add(files); }
  };
  return { items, add, remove, uploadAll, reset, onPaste, setItems };
};

const fmtSec = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const bars = (id) => Array.from({ length: 28 }, (_, i) => 25 + ((([...String(id)].reduce((h, c) => h * 31 + c.charCodeAt(0), 7) >> (i % 16)) + i * 37) % 75));

export const VoicePlayer = ({ a, mine }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const total = Number((a.name || "").match(/voice-(\d+)s/)?.[1]) || 0;
  const [dur, setDur] = useState(total);
  const pct = dur ? Math.min(1, pos / dur) : 0;
  const toggle = () => { const el = ref.current; if (!el) return; if (el.paused) el.play(); else el.pause(); };
  return (
    <div className={`flex min-w-[210px] items-center gap-2.5 rounded-xl px-2 py-1.5 ${mine ? "bg-white/15" : "bg-[#f2f7f6]"}`} data-testid="chat-voice-note">
      <audio ref={ref} src={imgUrl(a.url)} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setPos(0); }}
        onTimeUpdate={(e) => setPos(e.target.currentTime)} onLoadedMetadata={(e) => isFinite(e.target.duration) && setDur(e.target.duration)} />
      <button type="button" onClick={toggle} className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${mine ? "bg-white text-[#0e8c7f]" : "bg-[#0e8c7f] text-white"}`} aria-label={playing ? "Pause" : "Play"} data-testid="voice-play-btn">
        {playing ? <Pause size={16} className="fill-current" /> : <Play size={16} className="ml-0.5 fill-current" />}
      </button>
      <div className="flex h-7 flex-1 items-center gap-[2px]">
        {bars(a.id).map((h, i) => (
          <span key={i} className={`w-[3px] rounded-full transition-colors ${i / 28 < pct ? (mine ? "bg-white" : "bg-[#0e8c7f]") : (mine ? "bg-white/40" : "bg-[#b9d6d0]")}`} style={{ height: `${h}%` }} />
        ))}
      </div>
      <span className={`w-9 shrink-0 text-right text-[11px] tabular-nums ${mine ? "text-white/80" : "text-[#6b7c79]"}`}>{fmtSec(playing || pos ? pos : dur)}</span>
    </div>
  );
};

const pickMime = () => ["audio/webm;codecs=opus", "audio/mp4", "audio/webm", "audio/ogg"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

export const VoiceRecorder = ({ onRecorded, onError, disabled, t = (s) => s, max = 120 }) => {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const st = useRef({});
  const stop = (send) => {
    const s = st.current;
    s.want = false;
    s.send = send;
    if (s.mr && s.mr.state !== "inactive") s.mr.stop();
  };
  const start = async (e) => {
    if (disabled) return;
    e.preventDefault();
    st.current = { want: true, x: e.clientX, chunks: [] };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!st.current.want) { stream.getTracks().forEach((tr) => tr.stop()); return; }
      const mime = pickMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const s = st.current;
      s.mr = mr; s.t0 = Date.now();
      mr.ondataavailable = (ev) => ev.data.size && s.chunks.push(ev.data);
      mr.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        clearInterval(s.timer);
        setRec(null); setSecs(0);
        const len = Math.round((Date.now() - s.t0) / 1000);
        if (!s.send || len < 1) return;
        const type = (mr.mimeType || "audio/webm").split(";")[0];
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        onRecorded(new File(s.chunks, `voice-${len}s.${ext}`, { type }));
      };
      mr.start(250);
      setRec(true);
      s.timer = setInterval(() => {
        const n = Math.floor((Date.now() - s.t0) / 1000);
        setSecs(n);
        if (n >= max) stop(true);
      }, 250);
    } catch {
      onError?.(t("Microphone access is blocked. Please allow it in your browser settings."));
    }
  };
  const move = (e) => { if (rec && st.current.x - e.clientX > 90) stop(false); };
  return (
    <>
      {rec && (
        <div className="absolute inset-x-3 bottom-3 z-[4] flex h-11 items-center gap-3 rounded-full bg-[#fff1f0] px-4 text-[13.5px] text-[#b42318] sw-fade-up" data-testid="voice-recording-bar">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
          <span className="tabular-nums font-semibold">{fmtSec(secs)}</span>
          <span className="flex-1 truncate text-[12.5px] text-[#7a271a]">{t("Release to send · slide left to cancel")}</span>
        </div>
      )}
      <button type="button" disabled={disabled} onPointerDown={start} onPointerUp={() => stop(true)} onPointerLeave={() => rec && stop(true)} onPointerMove={move} onContextMenu={(e) => e.preventDefault()}
        className={`relative z-[5] flex h-11 w-11 shrink-0 touch-none select-none items-center justify-center rounded-full text-white transition-transform disabled:opacity-50 ${rec ? "scale-125 bg-red-500" : "bg-[#0e8c7f] hover:bg-[#0b7569]"}`} aria-label={t("Hold to record a voice note")} title={t("Hold to record a voice note")} data-testid="voice-record-btn">
        <Mic size={18} />
      </button>
    </>
  );
};

export const TypingBubble = ({ label }) => (
  <div className="flex items-center gap-2 text-[12.5px] text-[#6b7c79]" data-testid="typing-indicator">
    <span className="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-[#e5f2ef] bg-white px-3 py-2.5">
      {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#0e8c7f]" style={{ animationDelay: `${i * 150}ms` }} />)}
    </span>
    <span className="italic">{label}</span>
  </div>
);

export const Ticks = ({ seen, label }) => (
  <span className={`ml-1 inline-flex items-center gap-0.5 ${seen ? "text-[#a7f3d0]" : ""}`} data-testid={seen ? "msg-seen" : "msg-sent"}>
    {seen ? <CheckCheck size={13} /> : <Check size={13} />}{label}
  </span>
);

export const useTypingPing = (url) => {
  const last = useRef(0);
  return () => {
    if (!url || Date.now() - last.current < 2500) return;
    last.current = Date.now();
    api.post(url).catch(() => {});
  };
};

export const EDIT_WINDOW_MS = 15 * 60 * 1000;
export const canEdit = (m, mine) => mine && !m.deleted && !m.forwarded && !!m.text && Date.now() - new Date(m.created_at).getTime() < EDIT_WINDOW_MS;
export const hasMedia = (m) => !m.deleted && ((m.attachments || []).length > 0 || (m.images || []).length > 0);

export const menuFor = (m, mine, extra = []) => [
  "info", "reply", ...(m.text ? ["copy"] : []), "react", ...(hasMedia(m) ? ["download"] : []), ...(canEdit(m, mine) ? ["edit"] : []), ...extra,
];

export const downloadMedia = async (m) => {
  const atts = [...(m.attachments || []), ...(m.images || []).filter((u) => !(m.attachments || []).some((a) => a.url === u)).map((u) => ({ url: u, name: u.split("/").pop() }))];
  for (const a of atts) {
    const blob = await (await fetch(imgUrl(a.url))).blob();
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = a.name || "attachment";
    el.click();
    URL.revokeObjectURL(el.href);
  }
};

export const MenuArrow = ({ onOpen, mine }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); onOpen(mine ? r.right - 240 : r.left, r.bottom + 4); }}
    className={`absolute right-1.5 top-1.5 z-[2] hidden h-6 w-6 items-center justify-center rounded-full opacity-0 transition-opacity group-hover:opacity-100 md:flex ${mine ? "bg-[#0e8c7f] text-white" : "bg-white text-[#4b5d5a] shadow-sm"}`}
    aria-label="Message options" data-testid="msg-menu-arrow">
    <ChevronDown size={16} />
  </button>
);

const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : null);

export const MessageInfo = ({ info, onClose, t = (s) => s }) => {
  if (!info) return null;
  const { m, readAt, who } = info;
  const seen = readAt && m.created_at <= readAt ? readAt : null;
  const rows = [[t("From"), who], [t("Sent"), fmtFull(m.created_at)], ...(readAt === undefined ? [] : [[t("Seen"), seen ? fmtFull(seen) : t("Not seen yet")]]), ...(m.edited_at ? [[t("Edited"), fmtFull(m.edited_at)]] : [])];
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-[#0f2e2a]/40 p-5" onClick={onClose} data-testid="message-info-dialog">
      <div className="w-full max-w-[340px] rounded-2xl bg-white p-5 shadow-2xl sw-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center gap-2"><Info size={17} className="text-[#0e8c7f]" /><span className="flex-1 font-bold text-[#0f2e2a]">{t("Message info")}</span><button onClick={onClose} aria-label="Close" data-testid="message-info-close"><X size={17} /></button></div>
        {m.text && <div className="mb-3 max-h-24 overflow-hidden rounded-xl bg-[#f2f7f6] px-3 py-2 text-[13.5px] text-[#1f3a36]">{m.text}</div>}
        <dl className="space-y-2 text-[13.5px]">
          {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-[#6b7c79]">{k}</dt><dd className="text-right font-semibold text-[#0f2e2a]" data-testid={`info-${k.toLowerCase()}`}>{v}</dd></div>)}
        </dl>
      </div>
    </div>
  );
};
